import { useCallback, useRef, useState, type RefObject } from 'react';

export type InlineRenameTrigger = 'click' | 'mousedown';

export interface UseInlineRenameOptions {
	value: string;
	editable?: boolean;
	onCommit: (trimmed: string) => void;
	// Called before entering edit mode; return false to block.
	onBeforeStart?: () => boolean;
	trigger?: InlineRenameTrigger;
}

export interface UseInlineRenameResult {
	isEditing: boolean;
	startEditing: () => void;
	cancelEditing: () => void;
	commitEditing: () => void;
	inputRef: RefObject<HTMLSpanElement | null>;
	inputProps: {
		ref: (el: HTMLSpanElement | null) => void;
		contentEditable: true;
		suppressContentEditableWarning: true;
		onInput: () => void;
		onMouseDown: (e: React.MouseEvent) => void;
		onBlur: () => void;
		onKeyDown: (e: React.KeyboardEvent) => void;
	};
	wrapperProps: {
		onClick?: (e: React.MouseEvent) => void;
		onMouseDown?: (e: React.MouseEvent) => void;
	};
}

// Shared click-to-rename state for contentEditable inline titles.
export const useInlineRename = ({
	value,
	editable = true,
	onCommit,
	onBeforeStart,
	trigger = 'click',
}: UseInlineRenameOptions): UseInlineRenameResult => {
	const [isEditing, setIsEditing] = useState(false);
	const inputRef = useRef<HTMLSpanElement | null>(null);
	const draftRef = useRef('');
	// Set true by onKeyDown when it handles Enter/Escape so the follow-on onBlur
	// (focus leaves the input as a side-effect of the keypress) doesn't
	// double-commit (Enter case) or commit-after-cancel (Escape case).
	const keyboardExitRef = useRef(false);

	const commitEditing = useCallback(() => {
		const trimmed = draftRef.current.trim();
		setIsEditing(false);
		if (trimmed) {
			onCommit(trimmed);
		}
	}, [onCommit]);

	const cancelEditing = useCallback(() => {
		setIsEditing(false);
	}, []);

	const startEditing = useCallback(() => {
		if (!editable) return;
		if (onBeforeStart && !onBeforeStart()) return;
		draftRef.current = value;
		setIsEditing(true);
	}, [editable, onBeforeStart, value]);

	const onInputMount = useCallback((el: HTMLSpanElement | null) => {
		inputRef.current = el;
		if (!el) return;
		el.textContent = draftRef.current;
		el.focus();
		// Select-all on enter edit (not caret-at-click): we swap read-only label → contentEditable
		// on first click, so the click never lands in the field. Mapping click → caret would need
		// caretRangeFromPoint and breaks down with truncated/ellipsis titles. Select-all matches
		// rename-short-label patterns (tabs, files); type to replace or click again to adjust.
		const range = document.createRange();
		range.selectNodeContents(el);
		const sel = window.getSelection();
		sel?.removeAllRanges();
		sel?.addRange(range);
	}, []);

	const onEditMouseDown = useCallback((e: React.MouseEvent) => {
		// Keep mousedown on the field from bubbling to overlay / island chrome.
		e.stopPropagation();
	}, []);

	const onInput = useCallback(() => {
		draftRef.current = inputRef.current?.textContent ?? '';
	}, []);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				keyboardExitRef.current = true;
				commitEditing();
			} else if (e.key === 'Escape') {
				e.preventDefault();
				keyboardExitRef.current = true;
				cancelEditing();
			}
		},
		[commitEditing, cancelEditing]
	);

	const onBlur = useCallback(() => {
		// Skip blur-commit if a keyboard handler already exited edit mode —
		// otherwise Enter double-commits and Escape commits after cancel.
		if (keyboardExitRef.current) {
			keyboardExitRef.current = false;
			return;
		}
		commitEditing();
	}, [commitEditing]);

	const onTrigger = useCallback(
		(e: React.MouseEvent) => {
			if (trigger === 'mousedown') {
				e.preventDefault();
			}
			startEditing();
		},
		[trigger, startEditing]
	);

	const wrapperProps =
		trigger === 'mousedown'
			? { onMouseDown: isEditing ? undefined : onTrigger }
			: { onClick: isEditing ? undefined : onTrigger };

	return {
		isEditing,
		startEditing,
		cancelEditing,
		commitEditing,
		inputRef,
		inputProps: {
			ref: onInputMount,
			contentEditable: true,
			suppressContentEditableWarning: true,
			onInput,
			onMouseDown: onEditMouseDown,
			onBlur,
			onKeyDown,
		},
		wrapperProps,
	};
};
